import { useCallback, useEffect, useRef, useState, RefObject } from 'react';
import * as tus from 'tus-js-client';
import { v4 as uuidv4 } from 'uuid';

import config from '../../../config/path';
import { getAuthKey } from '../../../utils/auth';

import type {
    UploadedFileMeta,
    UploadChangeEvent,
    UploadVisualState,
    UploadTranslations,
    FileUploaderProps,
    UploadState,
} from '../types';

interface UseFileUploadArgs {
    onSuccess: FileUploaderProps['onSuccess'];
    onValidationError?: FileUploaderProps['onValidationError'];
    onLocalFilesChange?: FileUploaderProps['onLocalFilesChange'];
    onFilesChange?: FileUploaderProps['onFilesChange'];
    autoStart: boolean;
    onlyZip?: boolean;
    existingFiles?: string[];
    translations: UploadTranslations;
}

interface UploadActions {
    handleFileSelect: (event: UploadChangeEvent) => void;
    handleStart: () => void;
    handleReset: () => void;
    triggerRetry: (dropzoneRef: RefObject<HTMLDivElement>) => void;
    validateFile: (file: File) => boolean;
}

interface UseFileUploadResult {
    state: UploadState;
    actions: UploadActions;
    fileList: any[];
    hasUpload: boolean;
}

export function useFileUpload({
    onSuccess,
    onValidationError,
    onLocalFilesChange,
    onFilesChange,
    autoStart,
    onlyZip,
    existingFiles,
    translations,
}: UseFileUploadArgs): UseFileUploadResult {
    const token = getAuthKey();

    const [fileList, setFileList] = useState<any[]>([]);
    const [visualState, setVisualState] = useState<UploadVisualState>('idle');
    const [progress, setProgress] = useState(0);
    const [localError, setLocalError] = useState<string | null>(null);
    const [statusSubtitle, setStatusSubtitle] = useState<string | null>(null);
    const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
    const [currentFileName, setCurrentFileName] = useState<string | null>(null);

    const uploadRef = useRef<tus.Upload | null>(null);

    const notifyLocalFiles = useCallback(
        (files: UploadedFileMeta[]) => {
            onLocalFilesChange?.(files);
        },
        [onLocalFilesChange]
    );

    useEffect(() => {
        if (Array.isArray(existingFiles) && existingFiles.length) {
            setUploadedFiles(existingFiles.filter(Boolean));
        }
    }, [existingFiles]);

    useEffect(() => {
        onFilesChange?.(uploadedFiles);
    }, [uploadedFiles, onFilesChange]);

    const abortCurrentUpload = useCallback(() => {
        if (!uploadRef.current) return;
        try {
            uploadRef.current.abort();
        } catch (e) {
            console.warn('Abort upload error', e);
        } finally {
            uploadRef.current = null;
        }
    }, []);

    const handleReset = useCallback(() => {
        abortCurrentUpload();
        setFileList([]);
        setProgress(0);
        setVisualState('idle');
        setStatusSubtitle(null);
        setCurrentFileName(null);
        setLocalError(null);
        setUploadedFiles([]);
        notifyLocalFiles([]);
        onSuccess(null);
    }, [abortCurrentUpload, notifyLocalFiles, onSuccess]);

    const validateFile = useCallback(
        (file: File): boolean => {
            if (!onlyZip) {
                onValidationError?.(null);
                setLocalError(null);
                return true;
            }

            const isZip =
                file.name.toLowerCase().endsWith('.zip') ||
                file.type.includes('zip');

            if (!isZip) {
                const msg = translations.zipOnly;
                setVisualState('error');
                setStatusSubtitle(msg);
                setLocalError(msg);
                onValidationError?.(msg);
                return false;
            }

            onValidationError?.(null);
            setLocalError(null);
            return true;
        },
        [onlyZip, onValidationError, translations.zipOnly]
    );

    const handleFileSelect = useCallback(
        (event: UploadChangeEvent) => {
            const snapshot = [...event.fileList];
            const lastItem = snapshot.pop();
            const file = lastItem?.originFileObj as File | undefined;

            if (!lastItem) {
                setFileList([]);
                notifyLocalFiles([]);
                return;
            }

            setFileList([{ ...lastItem }]);

            if (!file) return;

            if (!validateFile(file)) return;

            abortCurrentUpload();

            const uploadedAt = new Date().toISOString();
            notifyLocalFiles([
                {
                    name: file.name,
                    size: file.size,
                    uploadedAt,
                },
            ]);

            setProgress(0);
            setCurrentFileName(file.name);
            setVisualState('uploading');
            setStatusSubtitle(null);
            setLocalError(null);
            setUploadedFiles([file.name]);

            const uniqueId = uuidv4();

            const upload = new tus.Upload(file, {
                endpoint: `${config.API}/api/file-upload`,
                headers: { Authorization: `Bearer ${token}` },
                chunkSize: 25 * 1024 * 1024,
                metadata: {
                    filename: `${uniqueId}_${file.name}`,
                    filetype: file.type,
                },
                removeFingerprintOnSuccess: true,
                storeFingerprintForResuming: false,
                onProgress: (bytesSent, bytesTotal) => {
                    const percent = Number(
                        ((bytesSent / bytesTotal) * 100).toFixed(2)
                    );
                    setProgress(percent);
                },
                onSuccess: () => {
                    const id = upload.url?.split('/').pop() || file.name;
                    setVisualState('success');
                    setStatusSubtitle(file.name);
                    setFileList([]);
                    onSuccess(id);
                    uploadRef.current = null;
                },
                onError: err => {
                    console.error('Upload error:', err);
                    const msg = translations.uploadFailedCommon;
                    setProgress(0);
                    setVisualState('error');
                    setStatusSubtitle(msg);
                    setLocalError(msg);
                    onValidationError?.(msg);
                    uploadRef.current = null;
                },
            });

            uploadRef.current = upload;

            if (autoStart) {
                try {
                    upload.start();
                } catch (e) {
                    console.error('autostart error:', e);
                }
            }
        },
        [
            abortCurrentUpload,
            validateFile,
            notifyLocalFiles,
            token,
            onSuccess,
            onValidationError,
            translations.uploadFailedCommon,
            autoStart,
        ]
    );

    const handleStart = useCallback(() => {
        if (!uploadRef.current) return;
        try {
            uploadRef.current.start();
        } catch (e) {
            console.error('manual start error:', e);
        }
    }, []);

    const triggerRetry = useCallback(
        (dropzoneRef: RefObject<HTMLDivElement>) => {
            handleReset();
            const wrapper = dropzoneRef.current?.closest(
                'span.ant-upload-wrapper'
            );
            const input = wrapper?.querySelector(
                'input[type="file"]'
            ) as HTMLInputElement | null;
            input?.click();
        },
        [handleReset]
    );

    const state: UploadState = {
        visualState,
        progress,
        localError,
        statusSubtitle,
        uploadedFiles,
        currentFileName,
    };

    return {
        state,
        actions: {
            handleFileSelect,
            handleStart,
            handleReset,
            triggerRetry,
            validateFile,
        },
        fileList,
        hasUpload: !!uploadRef.current,
    };
}
